import Singleton from '@core/services/common/Singleton';
import { instanceGuard } from '@core/utils/services';
import ServerConfig from '@core/models/country/ServerConfig';
import HostService from './_private/HostService';
import router, { links, routeNames } from '@src/app/router';
import Modals from 'src/app/store/modules/ui/shared/modal/modals';
import store from '@app/store';
import RetailService from '../retail/RetailService';
import { prepareTicketForPrint } from '../gravity/helpers/ticket';
import Ticket from '@core/models/tickets/Ticket';
import { get as _get } from '@lodash';
import i18n from '@src/app/localization/i18n';
import { redactAuthTokenAtPath } from './utils';
import BettingAvailableService from '../betting/BettingAvailableService';
import { NotReadySource } from '../betting/_private/enums';
import { INotification, IPlatform } from '../platform/IPlatform';
import recreateTicket from './_private/RecreateTicket';
import { IBalanceChange, IOnStatus, IPayload, IPrintOutcome, ITerminalConfig } from './models/Payload';
import FeatureFlagService from '../flags/FeatureFlagService';
import * as Sentry from '@sentry/vue';
import common from '@src/config/common';

class ProductService implements IPlatform {
  private static instance: ProductService;
  private slaveId: string;
  private terminalBalance: number;
  private terminalConfiguration: any = {};
  private authToken: string = '';

  private constructor(private config: ServerConfig) {
    BettingAvailableService.getInstance().set(NotReadySource.Printer, {
      reason: 'default printer off',
      available: false,
    });
    BettingAvailableService.getInstance().set(NotReadySource.RetailConn, {
      reason: 'default connection to retail',
      available: false,
    });

    this.setSlaveId(window.name);
    console.log('SSBT::Slave Id set to >>', this.slaveId);
    HostService.connect('http://localhost:3000', { query: { productName: this.slaveId } });
    HostService.onConnected((payload: any) => {
      console.log(payload);
      Sentry.setTag('productId', this.slaveId);
    });
    HostService.onTerminalConfig(async (payload: IPayload<ITerminalConfig>) => {
      const config = _get(payload, 'data', null);
      if (!config) return;
      console.log('SSBT::Terminal config received >>', redactAuthTokenAtPath(payload, 'data.authToken'));
      const { storeId, terminalId, retailDetails, authToken } = config;
      localStorage.setItem('storeId', storeId);
      Sentry.setTag('storeId', storeId);
      localStorage.setItem('terminalId', terminalId);
      Sentry.setTag('terminalId', terminalId);
      this.terminalConfiguration = {
        storeId,
        terminalId,
        retailDetails,
        authToken: '',
      };
      this.authToken = authToken;

      FeatureFlagService.getInstance().setInHouseCtx(
        common.app.ldClientId,
        { ...config.retailDetails.terminal, productId: this.getSlaveId() },
        config.retailDetails.shop,
        () => store.dispatch('data/flags/initFlags', null, { root: true }),
      );

      if (this.getSlaveId() === 'CheckTicket') return;
      RetailService.createInstance(this.config, {
        product: this.getSlaveId(),
        deviceUuid: this.getDeviceUUID(),
      });
    });
    HostService.onBalanceChanged((payload: IPayload<IBalanceChange>) => {
      console.log('SSBT::Balance changed >>', payload);
      const balance = _get(payload, 'data.balance', null);
      this.setTerminalBalance(balance);
    });
    HostService.onScanOutcome((payload: any) => {
      console.log('SSBT::Scan outcome >>', payload);
      const {
        data: { value },
      } = payload;
      this.checkTicket(value);
    });
    HostService.onPrintTicketOutcome((payload: IPayload<IPrintOutcome>) => {
      console.log('SSBT::Print ticket outcome >>', payload);
      const { status } = payload;
      if (status >= 200 && status < 300) {
        const title = i18n.t('print success').toString().toUpperCase();
        const text = i18n.t('ticket print success').toString().toUpperCase();
        this.sendNotification({ title, text, type: 'success' });
      } else {
        const title = i18n.t('ticket error').toString().toUpperCase();
        const text = i18n.t('ticket failed to print').toString().toUpperCase();
        this.sendNotification({ title, text, type: 'error' });
      }
      this.resetBetslip();
      store.dispatch('data/tickets/setTicketSubmitLoading', false);
    });
    HostService.onTicketRebetOutcome((payload: any) => {
      console.log('SSBT::Rebet ticket outcome >>', payload);
      const {
        data: { value },
      } = payload;
      recreateTicket(value);
    });
    HostService.onStatus((payload: IPayload<IOnStatus>) => {
      console.log('SSBT::Status Update Received', payload);
      store.dispatch('data/country/setStatuses', { payload: payload.data }, { root: true });
    });
    HostService.onNavigate((payload: any) => {
      console.log('SSBT::On navigate ~>', payload);
      const product = _get(payload, 'data.value.product.id', null);
      if (product && product === 'SuperbetInPlay' && window.name === 'SuperbetPrematch') {
        router.push({ path: links.live });
      }
      if (product && product === 'SuperbetPrematch' && window.name === 'SuperbetPrematch') {
        router.push({ path: links.landing });
      }
      if (product && product === 'HomeScreen') {
        this.checkTicket('CLEAR');
      }
    });
    HostService.onCardSerialNumberOutcome((payload: any) => {
      console.log('SSBT::On card serial number ~>', payload);
      const serialNumber = _get(payload, 'data.value', '');
      store.dispatch('data/country/setUserCardSerialNumber', serialNumber, { root: true });
    });
    HostService.emitBalanceStatusRequest();
    HostService.emitTerminalConfigRequest();
    HostService.emitStatusRequest();

    BettingAvailableService.getInstance().unset(NotReadySource.Printer);
  }

  public async resetBetslip() {
    await store.dispatch('ui/sportOffer/betSlip/clear', null, { root: true });
    await store.dispatch('ui/lotto/betSlip/clear', null, { root: true });
  }

  getGravityAuth(): string {
    throw new Error('Method not implemented.');
  }
  postMessage(message: any, source?: any): void {
    throw new Error('Method not implemented.');
  }

  public static getInstance(): ProductService {
    return instanceGuard(ProductService.instance);
  }

  public static createInstance(config: ServerConfig) {
    if (!ProductService.instance) {
      ProductService.instance = new ProductService(config);
    }
    return ProductService.instance;
  }

  public requestBalanceStatusRequest() {
    HostService.emitBalanceStatusRequest();
  }

  private checkTicket(id: string) {
    if (this.slaveId !== 'CheckTicket') return;
    store.dispatch('data/country/setCheckedTicket', { id, timeChecked: Date.now() }, { root: true });
  }

  public getTerminalConfiguration(): any {
    return this.terminalConfiguration;
  }

  public getAuthToken(): string {
    return this.authToken;
  }

  public getDeviceUUID() {
    return this.terminalConfiguration.retailDetails.terminal.externalId;
  }

  public setDeviceUUID(uuid: string) {}

  public getSlaveId(): string {
    return this.slaveId;
  }
  public setSlaveId(value: string) {
    this.slaveId = value;
  }

  public areFoundsAvailable(amount: number): boolean {
    console.log('SSBT::Current Terminal Balance', this.getTerminalBalance());
    return this.getTerminalBalance() >= amount;
  }

  public getTerminalBalance(): number {
    return this.terminalBalance;
  }

  public setTerminalBalance(value: number) {
    this.terminalBalance = value;
  }

  public checkTicketExit() {
    const name = window.name === 'SuperbetPrematch' ? routeNames.landing : routeNames.lotteryLanding;
    router.push({ name });
  }

  public sendNotification({ title, text, type }: INotification) {
    const notification = {
      title,
      text,
      type: type || 'info',
    };
    HostService.emitNotificationDispatchRequest(notification);
  }

  public checkTicketCancel = (ticketId?: string) => {
    const root = { root: true };
    const config = {
      code: Modals.cancelTicket.code,
      containerClass: 'cancle-ticket-modal--terminal',
      data: {
        ticketId,
        button: {
          label: 'confirm',
          enabled: true,
          className: 'btn modal-btn--terminal btn--block btn--cancel-ticket',
        },
      },
    };
    store.dispatch('ui/modal/setModal', config, root);
  };

  public checkPayoutTicket(ticketId?: string) {
    const root = { root: true };
    const config = {
      code: Modals.payoutTicket.code,
      containerClass: 'payout-ticket-modal--terminal',
      data: {
        ticketId,
        button: {
          label: 'confirm',
          enabled: true,
          className: 'btn modal-btn--terminal btn--block btn--payout-ticket',
        },
      },
    };
    store.dispatch('ui/modal/setModal', config, root);
  }

  public checkTicketRecreateRequest(ticketId: string, product: string) {
    HostService.emitTicketRebetRequest({ ticketId, productId: product });
  }

  public async sendTicketPrint(ticket: Ticket | any): Promise<void> {
    const preparedTicket = await prepareTicketForPrint(ticket, this.config);
    console.log('SSBT::Prepared Ticket for Print >>', preparedTicket);
    HostService.emitPrintTicketRequest(preparedTicket);
  }

  public scanVoucher(voucherId: string) {
    const payload = { value: voucherId };
    console.log('SSBT::Scanning Voucher Manual ~>', payload);
    HostService.emitVoucherScanRequest(payload);
  }

  public static clearInstance() {
    if (process.env.NODE_ENV !== 'test') {
      throw new Error('For use in tests only');
    }
    delete ProductService.instance;
  }
}

export default ProductService as Singleton<ProductService>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/terminal/core/services/product/ProductService.ts